import React from 'react';
import { Link } from '../router.jsx';
import { apiFetch } from '../api.js';
import { sanitizeCmsHtml } from '../lib/sanitizeCmsHtml.js';

export default function Landing() {
  const [welcomeContent, setWelcomeContent] = React.useState('');
  const safeWelcomeContent = React.useMemo(() => sanitizeCmsHtml(welcomeContent), [welcomeContent]);

  React.useEffect(() => {
    let active = true;
    apiFetch('/api/pages/welcome')
      .then((page) => {
        if (!active) return;
        const content = String(page?.content || '').trim();
        if (content) {
          setWelcomeContent(content);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="landing">
      <section className="hero">
        <div className="hero-content">
          <div className="eyebrow">ECE Portal</div>
          <h1 className="hero-heading">
            Electrical &amp; Computer Engineering Department
          </h1>
          {welcomeContent ? (
            <div className="hero-sub cms-content" dangerouslySetInnerHTML={{ __html: safeWelcomeContent }} />
          ) : (
            <p className="hero-sub">
              The ECE department at Cal State LA provides a solid foundation in electrical and computer engineering,
              offering a well-rounded, up-to-date curriculum that balances theory and practice, preparing you to
              innovate and lead in the field.
            </p>
          )}
          <div className="hero-actions">
            <Link to="/login/student" className="btn btn-primary btn-lg">Student Login</Link>
            <Link to="/login/staff" className="btn btn-secondary btn-lg">Advisor Login</Link>
          </div>
        </div>
        <div className="hero-panel">
          <div className="hero-image-wrap">
            <img src="/images/ee_header.png" alt="ECE Department" className="landing-hero-image" />
          </div>
        </div>
      </section>

      <section className="feature-grid">
        <div className="feature-card">
          <div className="feature-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
            </svg>
          </div>
          <h3 className="feature-title">Graduate Study Plan</h3>
          <p className="feature-desc">
            Set up and manage your study plan. Use your Cal State LA portal credentials to log in
            after your advisor creates a profile for you.
          </p>
          <a href="https://www.calstatela.edu/ecst/ece/student-handbook-0" target="_blank" rel="noreferrer" className="feature-link">
            View MSEE Requirements
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M7 17L17 7M17 7H7M17 7v10" /></svg>
          </a>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
          </div>
          <h3 className="feature-title">Quick Links</h3>
          <div className="feature-links">
            <a href="https://www.calstatela.edu/ecst/ece" target="_blank" rel="noreferrer">ECE Website</a>
            <a href="https://www.calstatela.edu/ecst/ece/contacts" target="_blank" rel="noreferrer">Faculty &amp; Staff</a>
            <Link to="/login/student">Student Login</Link>
            <Link to="/login/staff">Advisor Login</Link>
          </div>
        </div>
        <div className="feature-card">
          <div className="feature-icon">
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <h3 className="feature-title">Contact Us</h3>
          <p className="feature-desc">
            The ECE Department Office (E &amp; T A-342) is open 9 am &ndash; 6 pm, Monday through Friday.
          </p>
          <div className="feature-contact">
            <span>TEL: (323) 343-4470</span>
            <span>FAX: (323) 343-4547</span>
            <a href="mailto:ee.ecst@calstatela.edu">ee.ecst@calstatela.edu</a>
          </div>
        </div>
      </section>
    </div>
  );
}
